import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

export function ResourceForm({ open, onOpenChange, title, fields, initial, onSubmit, submitting }) {
  const [values, setValues] = useState({});

  useEffect(() => {
    if (open) {
      const base = {};
      fields.forEach((f) => {
        base[f.name] = initial?.[f.name] ?? f.default ?? (f.type === "switch" ? true : "");
      });
      setValues(base);
    }
  }, [open, initial, fields]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    onSubmit(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="resource-form">
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.name} className={cn("space-y-1.5", f.span === 1 ? "col-span-1" : "col-span-2")}>
              <Label className="eyebrow" htmlFor={f.name}>{f.label}{f.required && <span className="text-primary"> *</span>}</Label>
              {f.type === "select" ? (
                <Select value={values[f.name] ? String(values[f.name]) : ""} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder={f.placeholder || "Select"} /></SelectTrigger>
                  <SelectContent>
                    {(f.options || []).map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "textarea" ? (
                <Textarea id={f.name} data-testid={`field-${f.name}`} value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} />
              ) : f.type === "switch" ? (
                <div className="flex h-9 items-center"><Switch checked={!!values[f.name]} onCheckedChange={(v) => set(f.name, v)} data-testid={`field-${f.name}`} /></div>
              ) : (
                <Input
                  id={f.name}
                  data-testid={`field-${f.name}`}
                  type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                  step={f.step}
                  required={f.required}
                  value={values[f.name] ?? ""}
                  onChange={(e) => set(f.name, f.type === "number" ? e.target.value : e.target.value)}
                  placeholder={f.placeholder}
                />
              )}
            </div>
          ))}
          <DialogFooter className="col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="form-submit">{submitting ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
